/**
 * Recommendations feedback bridge.
 *
 * Batches storefront recommendation impression and click events and posts them
 * to the Garden v2 recommendation feedback endpoint so the ranker can learn
 * from real user behavior (CTR metrics + per-user cool-down state).
 *
 * Listens for:
 *   - `mm:recommendations:impression` – dispatched by cart + contextual rails.
 *     `detail.variant` must be "cart" | "pdp"; `detail.recommendations` is an
 *     array of `{ productId, handle?, reason?, reasonSource?, score? }`.
 *   - `mm:recommendations:click` – single-product click.
 *
 * Also delegates click tracking to any element tagged with
 *   [data-mm-rec-product-id] or [data-rec-product-id]
 * so rails that don't dispatch custom events (e.g. the personalized-home
 * trending rail) still contribute click feedback.
 *
 * Server contract (app proxy, see garden/app/routes/app_proxy/garden/v1.recommendations.feedback.tsx):
 *   POST /apps/garden/v1/recommendations/feedback
 *   {
 *     "event":        "impression" | "click",
 *     "surface":      "home" | "pdp" | "cart",
 *     "productGids":  ["gid://shopify/Product/123", ...],
 *     "reasonSources": ["zone", "copurchase", ...],
 *     "timestampMs":  1700000000000
 *   }
 *
 * The server derives the customer uid from the signed App Proxy context — no
 * client-supplied identifier is needed.
 *
 * Recommendation GET requests are read-only. This bridge is the impression
 * owner; the client and backend dedupe impressions before updating per-user
 * cool-down state. Clicks are never deduped.
 */
(function () {
  'use strict';

  if (window.__mmRecsFeedbackInit) return;
  window.__mmRecsFeedbackInit = true;

  var ENDPOINT = '/apps/garden/v1/recommendations/feedback';
  var FLUSH_INTERVAL_MS = 1500;
  var MAX_QUEUE = 200;
  var MAX_BATCH_PER_GROUP = 25;
  var IMPRESSION_DEDUPE_PREFIX = 'mrmaple:recs-feedback:impression:';

  // Each queue entry: { type: 'impression'|'click', surface: 'home'|'pdp'|'cart',
  //                     productId: string, reasonSource?: string|null, ts: number }
  var queue = [];
  var flushTimer = null;
  var flushing = false;
  var inMemoryImpressionDedupe = Object.create(null);

  /* ── helpers ─────────────────────────────────────────────────────────── */

  function normalizeSurface(raw) {
    var s = String(raw || '').toLowerCase();
    if (s === 'cart') return 'cart';
    if (s === 'pdp' || s === 'contextual') return 'pdp';
    if (s === 'home' || s === 'trending' || s === 'personalized-home' || s === 'delegated') return 'home';
    return null;
  }

  function normalizeProductId(raw) {
    if (raw == null) return null;
    var s = String(raw).trim();
    if (!s) return null;
    if (/^gid:\/\/shopify\/Product\/\d+$/.test(s)) return s;
    if (/^\d{5,}$/.test(s)) return 'gid://shopify/Product/' + s;
    return null;
  }

  function scheduleFlush() {
    if (flushTimer) return;
    flushTimer = window.setTimeout(flush, FLUSH_INTERVAL_MS);
  }

  function getDayKey(ts) {
    try {
      return new Date(ts || Date.now()).toISOString().slice(0, 10);
    } catch (_) {
      return String(Math.floor((ts || Date.now()) / 86400000));
    }
  }

  function getImpressionDedupeKey(entry) {
    return [IMPRESSION_DEDUPE_PREFIX, getDayKey(entry.ts), entry.surface, entry.productId].join(':');
  }

  function shouldSkipImpression(entry) {
    var key = getImpressionDedupeKey(entry);

    try {
      if (window.sessionStorage && sessionStorage.getItem(key)) {
        return true;
      }
      if (window.sessionStorage) {
        sessionStorage.setItem(key, '1');
        return false;
      }
    } catch (_) {}

    if (inMemoryImpressionDedupe[key]) {
      return true;
    }
    inMemoryImpressionDedupe[key] = true;
    return false;
  }

  function enqueue(entry) {
    if (!entry || !entry.productId || !entry.surface) return;
    if (entry.type === 'impression' && shouldSkipImpression(entry)) return;
    if (queue.length >= MAX_QUEUE) queue.shift();
    queue.push(entry);
    scheduleFlush();
  }

  /* ── network ─────────────────────────────────────────────────────────── */

  function postPayload(payload) {
    var body = JSON.stringify(payload);
    try {
      if (navigator.sendBeacon) {
        var blob = new Blob([body], { type: 'application/json' });
        if (navigator.sendBeacon(ENDPOINT, blob)) return Promise.resolve(true);
      }
    } catch (_) {}

    return fetch(ENDPOINT, {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: body,
    })
      .then(function (res) {
        return res.ok;
      })
      .catch(function () {
        return false;
      });
  }

  function buildGroupPayloads(entries) {
    // Group by (type × surface). Each group becomes one POST matching the
    // server contract.
    var groups = {};
    for (var i = 0; i < entries.length; i++) {
      var e = entries[i];
      var key = e.type + '|' + e.surface;
      if (!groups[key]) {
        groups[key] = {
          event: e.type,
          surface: e.surface,
          productGids: [],
          reasonSources: [],
          timestampMs: e.ts || Date.now(),
          _seen: Object.create(null),
        };
      }
      var g = groups[key];
      if (e.type === 'impression') {
        if (g._seen[e.productId]) continue;
        g._seen[e.productId] = true;
      }
      if (g.productGids.length >= MAX_BATCH_PER_GROUP) continue;
      g.productGids.push(e.productId);
      if (e.reasonSource) g.reasonSources.push(String(e.reasonSource).slice(0, 48));
    }

    var payloads = [];
    for (var key2 in groups) {
      if (!Object.prototype.hasOwnProperty.call(groups, key2)) continue;
      var group = groups[key2];
      if (group.productGids.length === 0) continue;
      // Dedupe reasonSources while preserving order.
      var rsSeen = Object.create(null);
      var rs = [];
      for (var j = 0; j < group.reasonSources.length; j++) {
        var r = group.reasonSources[j];
        if (rsSeen[r]) continue;
        rsSeen[r] = true;
        rs.push(r);
      }
      payloads.push({
        event: group.event,
        surface: group.surface,
        productGids: group.productGids,
        reasonSources: rs,
        timestampMs: group.timestampMs,
      });
    }
    return payloads;
  }

  function flush() {
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    if (flushing) return;
    if (queue.length === 0) return;

    flushing = true;
    var entries = queue.splice(0, queue.length);
    var payloads = buildGroupPayloads(entries);

    Promise.all(payloads.map(postPayload))
      .then(function () {
        flushing = false;
        if (queue.length > 0) scheduleFlush();
      })
      .catch(function () {
        flushing = false;
      });
  }

  /* ── event listeners ─────────────────────────────────────────────────── */

  document.addEventListener('mm:recommendations:impression', function (evt) {
    var detail = (evt && evt.detail) || {};
    var surface = normalizeSurface(detail.variant);
    if (!surface) return;
    var recs = Array.isArray(detail.recommendations) ? detail.recommendations : [];
    var ts = Date.now();
    for (var i = 0; i < recs.length; i++) {
      var productId = normalizeProductId(recs[i] && (recs[i].productId || recs[i].id));
      if (!productId) continue;
      enqueue({
        type: 'impression',
        surface: surface,
        productId: productId,
        reasonSource: (recs[i] && recs[i].reasonSource) || null,
        ts: ts,
      });
    }
  });

  document.addEventListener('mm:recommendations:click', function (evt) {
    var detail = (evt && evt.detail) || {};
    var surface = normalizeSurface(detail.variant);
    if (!surface) return;
    var productId = normalizeProductId(detail.productId || detail.id);
    if (!productId) return;
    enqueue({
      type: 'click',
      surface: surface,
      productId: productId,
      reasonSource: detail.reasonSource || null,
      ts: Date.now(),
    });
  });

  // Delegated click tracking for rails that tag their cards but don't dispatch
  // the custom event (e.g. the personalized-home trending rail).
  document.addEventListener(
    'click',
    function (evt) {
      var target = evt.target;
      if (!target || typeof target.closest !== 'function') return;
      var card = target.closest('[data-mm-rec-product-id], [data-rec-product-id]');
      if (!card) return;
      // If the card lives inside a rail that already emits the custom event,
      // skip delegated tracking to avoid double counting.
      if (card.closest('.mm-cart-rec-card') || card.closest('.mm-contextual-card')) return;

      var productId = normalizeProductId(card.getAttribute('data-mm-rec-product-id') || card.getAttribute('data-rec-product-id'));
      if (!productId) return;
      var surface = normalizeSurface(card.getAttribute('data-mm-rec-surface') || card.getAttribute('data-rec-surface') || 'home');
      if (!surface) return;
      enqueue({
        type: 'click',
        surface: surface,
        productId: productId,
        reasonSource: card.getAttribute('data-rec-source') || null,
        ts: Date.now(),
      });
    },
    true,
  );

  // Flush on page hide (best-effort delivery).
  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') flush();
  });
})();
